import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, Paper, Button } from "@mui/material";
import { Add } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { deleteCarWash } from "../../../../services/carWash.service";
import { ICarWashObject } from "../../../../types/carWash";
import { useOwner } from "../../../../context/OwnerContext";
import CarWashForm from "../../../../components/Forms/CarWash";
import CarWashesTable from "./components/CarWashesTable";
import DeleteCarWashDialog from "./components/DeleteCarWashDialog";
import EmptyState from "./components/EmptyState";

const WashingCenters = () => {
  const navigate = useNavigate();
  const { owner, loading, error, setError, refetch } = useOwner();
  const { t } = useTranslation();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [carWashToDelete, setCarWashToDelete] = useState<ICarWashObject | null>(
    null
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const [addCarWashOpen, setAddCarWashOpen] = useState(false);

  const handleViewCarWash = (carWashId: string) => {
    navigate(`/dashboard/manage-car-washes/${carWashId}/info`);
  };

  const handleDeleteClick = (carWash: ICarWashObject) => {
    setCarWashToDelete(carWash);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!carWashToDelete?._id) return;

    setIsDeleting(true);
    try {
      await deleteCarWash(carWashToDelete._id);
      await refetch();
      setDeleteModalOpen(false);
      setCarWashToDelete(null);
    } catch (error: any) {
      setError(error.message || "Failed to delete car wash");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setCarWashToDelete(null);
  };

  if (loading) {
    return (
      <Box className="p-4">
        <Typography>{t("ownerScreens.loadingWashingCenters")}</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="p-4">
        <Paper className="p-6">
          <Box className="text-center">
            <Typography variant="h6" color="error" gutterBottom>
              {t("ownerScreens.errorLoadingWashingCenters")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {error}
            </Typography>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box className="p-4">
      <Box className="flex justify-between items-center mb-6">
        <Typography variant="h4" gutterBottom>
          {t("owners.detail.tabWashingCenters")}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setAddCarWashOpen(true)}
        >
          {t("ownerScreens.addWashingCenter")}
        </Button>
      </Box>

      {owner?.carWashes.length === 0 ? (
        <EmptyState />
      ) : (
        <CarWashesTable
          carWashes={owner?.carWashes || []}
          onView={handleViewCarWash}
          onDelete={handleDeleteClick}
        />
      )}

      <DeleteCarWashDialog
        open={deleteModalOpen}
        carWash={carWashToDelete}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        loading={isDeleting}
      />

      {addCarWashOpen && owner && (
        <CarWashForm
          ownerID={owner._id}
          lockedOwner
          isOpen={true}
          closeModal={async () => {
            setAddCarWashOpen(false);
            await refetch();
          }}
        />
      )}
    </Box>
  );
};

export default WashingCenters;
